import { Container, Title, Text, SimpleGrid, Paper, Group, Button, Box, Stack, Flex, Badge } from "@mantine/core";
import Link from "next/link";
import { IconBrain, IconPalette, IconUser, IconCamera, IconRobot, IconPhoto, IconArrowRight } from "@tabler/icons-react";
import type { ServiceId } from "@/lib/ownedServices";

export interface Tool {
  title: string;
  description: string;
  icon: any;
  link: string;
  buttonText: string;
  image?: string;
  premium?: boolean;
  serviceId?: ServiceId;
  price?: number;
}

interface ToolsGridProps {
  tools: Tool[];
  showPremiumSection?: boolean;
  showFreeSection?: boolean;
  showCoreSection?: boolean;
  containerSize?: "xl" | "lg" | "md" | "sm" | "xs";
  titleSize?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
}

export default function ToolsGrid({
  tools,
  showPremiumSection = true,
  showFreeSection = true,
  showCoreSection = true,
  containerSize = "xl",
  titleSize = "h2",
}: ToolsGridProps) {
  const coreTools = tools.filter((tool) => tool.serviceId);
  const premiumTools = tools.filter((tool) => tool.premium && !tool.serviceId);
  const freeTools = tools.filter((tool) => !tool.premium && !tool.serviceId);

  return (
    <Container size={containerSize} py={{ base: "md", sm: "xl" }}>
      <Stack gap="xl">
        {/* Core Tools Section */}
        {showCoreSection && coreTools.length > 0 && (
          <div>
            <Title order={2} ta="center" fz={{ base: "24px", sm: "28px" }}>
              Our 4 Core Tools
            </Title>
            <Text c="dimmed" ta="center" fz={{ base: "sm", sm: "md" }}>
              Get any tool individually, or save with the complete bundle.
            </Text>
            <Flex gap="lg" justify="center" align="center" wrap="wrap" style={{ minHeight: 300 }} pt="md">
              {coreTools.map((tool) => (
                <Paper
                  key={tool.title}
                  p="xl"
                  radius="md"
                  withBorder
                  style={{
                    borderColor: "var(--mantine-color-wumpus-6)",
                    borderWidth: "2px",
                    shadow: "lg",
                    minWidth: 300,
                    maxWidth: 350,
                  }}
                >
                  <Group justify="center" mb="md" pos="relative">
                    <Box
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        background: "var(--mantine-color-wumpus-1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <tool.icon size={32} color="var(--mantine-color-wumpus-6)" />
                    </Box>
                  </Group>
                  <Title order={3} ta="center" fw={600} mb="sm">
                    {tool.title}
                  </Title>
                  <Text size="sm" c="dimmed" ta="center" mb="md">
                    {tool.description}
                  </Text>
                  {tool.image && (
                    <Box mb="md" style={{ position: "relative", height: 160, overflow: "hidden", borderRadius: "8px" }}>
                      {tool.image.endsWith(".mp4") ? (
                        <video
                          src={tool.image}
                          autoPlay
                          loop
                          muted
                          playsInline
                          aria-label={tool.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <img
                          src={tool.image}
                          alt={tool.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      )}
                    </Box>
                  )}
                  <Group justify="center" mb="sm">
                    {tool.price != null && (
                      <Badge size="lg" variant="light" color="wumpus">
                        ${tool.price}
                      </Badge>
                    )}
                  </Group>
                  <Group justify="center">
                    <Button component={Link} color="wumpus" size="sm" href={tool.link} fullWidth variant="filled">
                      {tool.buttonText}
                    </Button>
                  </Group>
                </Paper>
              ))}
            </Flex>
            <Paper
              mt="lg"
              p="md"
              radius="md"
              style={{
                background: "linear-gradient(135deg, #fdf2f8 0%, #fff 50%, #f8fafc 100%)",
                border: "1px solid #fce7f3",
              }}
            >
              <Group justify="center" gap="sm">
                <Text fz="md" fw={500} style={{ color: "#1e293b" }}>
                  Or get all 4 together for $39
                </Text>
                <Button
                  component={Link}
                  href="/style-profile-bundle"
                  size="sm"
                  radius="xl"
                  rightSection={<IconArrowRight size={14} />}
                  style={{ background: "#1e293b", color: "white" }}
                >
                  See the Bundle
                </Button>
              </Group>
            </Paper>
          </div>
        )}

        {/* Premium Tools Section */}
        {showPremiumSection && premiumTools.length > 0 && (
          <div>
            <Title order={2} ta="center" fz={{ base: "24px", sm: "28px" }}>
              More Tools
            </Title>
            <Text c="dimmed" ta="center" fz={{ base: "sm", sm: "md" }}>
              Our advanced tools are designed to help you look your best.
            </Text>
            <Flex gap="lg" justify="center" align="center" wrap="wrap" style={{ minHeight: 300 }} pt="md">
              {premiumTools.map((tool) => (
                <Paper
                  key={tool.title}
                  p="xl"
                  radius="md"
                  withBorder
                  style={{
                    borderColor: "var(--mantine-color-wumpus-6)",
                    borderWidth: "2px",
                    shadow: "lg",
                    minWidth: 300,
                    maxWidth: 350,
                  }}
                >
                  <Group justify="center" mb="md" pos="relative">
                    <Box
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        background: "var(--mantine-color-wumpus-1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <tool.icon size={32} color="var(--mantine-color-wumpus-6)" />
                    </Box>
                  </Group>
                  <Title order={3} ta="center" fw={600} mb="sm">
                    {tool.title}
                  </Title>
                  <Text size="sm" c="dimmed" ta="center" mb="md">
                    {tool.description}
                  </Text>
                  {tool.image && (
                    <Box mb="md" style={{ position: "relative", height: 160, overflow: "hidden", borderRadius: "8px" }}>
                      {tool.image.endsWith(".mp4") ? (
                        <video
                          src={tool.image}
                          autoPlay
                          loop
                          muted
                          playsInline
                          aria-label={tool.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <img
                          src={tool.image}
                          alt={tool.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      )}
                    </Box>
                  )}
                  <Group justify="center">
                    <Button component={Link} color="wumpus" size="sm" href={tool.link} fullWidth variant="filled">
                      {tool.buttonText}
                    </Button>
                  </Group>
                </Paper>
              ))}
            </Flex>
          </div>
        )}

        {/* Free Tools Section */}
        {showFreeSection && freeTools.length > 0 && (
          <div>
            <Title order={2} ta="center" fz={{ base: "24px", sm: "28px" }}>
              Free Quizzes & Guides
            </Title>
            <Text c="dimmed" ta="center" fz={{ base: "sm", sm: "md" }} mb="sm">
              Simple tools to help you look your best.
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
              {freeTools.map((tool) => (
                <Paper
                  key={tool.title}
                  p="xl"
                  radius="md"
                  withBorder
                  style={{
                    borderColor: "var(--mantine-color-blue-6)",
                    borderWidth: "1px",
                  }}
                >
                  <Group justify="center" mb="md">
                    <Box
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        background: "var(--mantine-color-blue-1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <tool.icon size={32} color="var(--mantine-color-blue-6)" />
                    </Box>
                  </Group>
                  <Title order={3} ta="center" fw={600} mb="sm">
                    {tool.title}
                  </Title>
                  <Text size="sm" c="dimmed" ta="center" mb="md">
                    {tool.description}
                  </Text>
                  {tool.image && (
                    <Box mb="md" style={{ position: "relative", height: 160, overflow: "hidden", borderRadius: "8px" }}>
                      {tool.image.endsWith(".mp4") ? (
                        <video
                          src={tool.image}
                          autoPlay
                          loop
                          muted
                          playsInline
                          aria-label={tool.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <img
                          src={tool.image}
                          alt={tool.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      )}
                    </Box>
                  )}
                  <Group justify="center">
                    <Button component={Link} color="blue" size="sm" href={tool.link} fullWidth variant="light">
                      {tool.buttonText}
                    </Button>
                  </Group>
                </Paper>
              ))}
            </SimpleGrid>
          </div>
        )}
      </Stack>
    </Container>
  );
}
